import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Copy, Gift, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl bg-white/[0.04] p-3">
    <p className="text-[11px] text-slate-400">{label}</p>
    <p className="font-display text-lg font-bold" data-testid={testId}>{value}</p>
  </div>
);

const useReferral = () => {
  const { t, tErr } = useI18n();
  const qc = useQueryClient();
  const { data: r } = useQuery({ queryKey: ["referral-me"], queryFn: () => api.get("/referral/me").then((x) => x.data) });
  const transfer = async () => {
    try {
      const { data } = await api.post("/referral/transfer");
      toast.success(t("ref.transferred", { a: formatXAF(data.amount) }));
      qc.invalidateQueries({ queryKey: ["referral-me"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
    } catch (e) { toast.error(tErr(errCode(e))); }
  };
  return { r, transfer };
};

const TransferBtn = ({ r, transfer }) => {
  const { t } = useI18n();
  return (
    <button type="button" disabled={r.balance < r.min_transfer || r.blocked} onClick={transfer} className="h-11 w-full rounded-full bg-amber-500 text-sm font-bold text-[#0B0D12] disabled:opacity-40" data-testid="referral-transfer-btn">
      {r.balance < r.min_transfer ? t("ref.minTransfer", { m: formatXAF(r.min_transfer) }) : t("ref.transfer", { a: formatXAF(r.balance) })}
    </button>
  );
};

export const ReferralWalletCard = () => {
  const { t } = useI18n();
  const { r, transfer } = useReferral();
  if (!r || !r.has_account) return null;
  return (
    <section className="surface space-y-4 p-5 sm:p-6" data-testid="wallet-referral-account">
      <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-amber-400" /><h2 className="font-display text-lg font-bold">{t("ref.account")}</h2></div>
      <p className="text-sm text-slate-400">{t("ref.accountSub")}</p>
      <div className="grid grid-cols-3 gap-2">
        <Stat label={t("ref.balance")} value={formatXAF(r.balance)} testId="wallet-referral-balance" />
        <Stat label={t("ref.month")} value={formatXAF(r.month_earned)} testId="wallet-referral-month" />
        <Stat label={t("ref.filleuls")} value={r.filleuls} testId="wallet-referral-filleuls" />
      </div>
      <TransferBtn r={r} transfer={transfer} />
    </section>
  );
};

export const ReferralCard = () => {
  const { t } = useI18n();
  const { r, transfer } = useReferral();
  if (!r || !r.enabled) return null;
  const link = r.code ? `${window.location.origin}/?ref=${r.code}` : "";
  const wa = `https://wa.me/?text=${encodeURIComponent(`${t("ref.waText")} ${link}`)}`;
  return (
    <section className="surface space-y-4 p-5" data-testid="referral-card">
      <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-amber-400" /><h2 className="font-display text-lg font-bold">{t("ref.title")}</h2></div>
      {!r.eligible ? (
        <p className="text-sm text-slate-400" data-testid="referral-not-eligible">{r.blocked ? t("ref.blocked") : t("ref.needKyc")}</p>
      ) : (
        <>
          <p className="text-sm text-slate-400">{t("ref.pitch", { p: r.percent })}</p>
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
            <span className="min-w-0 flex-1 truncate font-mono text-sm" data-testid="referral-link">{link}</span>
            <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(link); toast.success(t("ref.copied")); } catch { toast.error(link); } }} className="rounded-full p-2 hover:bg-white/10" data-testid="referral-copy-btn"><Copy className="h-4 w-4" /></button>
          </div>
          <p className="text-xs text-slate-500">{t("ref.code")} <b className="font-mono text-slate-200" data-testid="referral-code">{r.code}</b></p>
          <a href={wa} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#25D366] text-sm font-semibold text-[#0B0D12]" data-testid="referral-whatsapp-btn"><MessageCircle className="h-4 w-4" />{t("ref.invite")}</a>
        </>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label={t("ref.filleuls")} value={r.filleuls} testId="referral-filleuls" />
        <Stat label={t("ref.active")} value={r.active_month} testId="referral-active" />
        <Stat label={t("ref.month")} value={formatXAF(r.month_earned)} testId="referral-month" />
        <Stat label={t("ref.balance")} value={formatXAF(r.balance)} testId="referral-balance" />
      </div>
      <TransferBtn r={r} transfer={transfer} />
      <Link to="/parrainage" className="block text-center text-sm text-amber-300 underline" data-testid="referral-rules-link">{t("ref.rules")}</Link>
    </section>
  );
};
